import React, { useContext, useEffect, useRef, useState } from "react";
import { Cascader, message } from "antd";
import { LoadingIcon, TopDropdown } from "../../../components";
import { getCrops, getOrgs, getSites } from "../../../utils/api";
import { UserContext } from "../../../context/UserContext";
import { useLocalStorage } from "../../../hooks";
import { useSearchParams } from "react-router-dom";

// Helper
const isDictionary = (variable) =>
  typeof variable === "object" && variable !== null && !Array.isArray(variable);

const metricValToLabel = { sum: "Cumulative", avg: "Average" };

const Dropdowns = ({
  setSiteMetadata,
  currentMetric,
  setCurrentMetric,
  metricType,
  setMetricType,
  fetchGraphData,
  currentOrg,
  setCurrentOrg,
  currentSite,
  setCurrentSite,
  currentCrop,
  setCurrentCrop,
  setDropdownLoading,
}) => {
  const [defaultDropdown, setDefaultDropdown] = useLocalStorage(
    "dropdowns",
    {},
  );
  const { userData } = useContext(UserContext);

  // Dropdown data and loading state
  const [orgData, setOrgData] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);

  const [siteData, setSiteData] = useState([]);
  const [siteLoading, setSiteLoading] = useState(false);

  const [cropData, setCropData] = useState([]);
  const [cropLoading, setCropLoading] = useState(false);

  const [metricData, setMetricData] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();

  // Keep track of last fetch to avoid race conditions
  const lastFetchRef = useRef({ org: 0, site: 0, crop: 0 });

  // ------------------------------------
  //  API fetch calls with race protection
  // ------------------------------------
  const fetchOrgs = async () => {
    const fetchId = ++lastFetchRef.current.org;
    setOrgLoading(true);
    try {
      const res = await getOrgs();
      if (fetchId !== lastFetchRef.current.org) return; // stale response
      const newData = res?.data?.data
        .filter((org) => org.is_active && org.is_breeder)
        .map((org) => ({
          name: org.name,
          id: org.id,
        }));
      setOrgData(newData);
    } catch (err) {
      if (fetchId !== lastFetchRef.current.org) return;
      message.error("Error fetching Organizations!");
      setOrgData([]);
    } finally {
      if (fetchId === lastFetchRef.current.org) setOrgLoading(false);
    }
  };

  const fetchSites = async (orgId) => {
    const fetchId = ++lastFetchRef.current.site;
    setSiteLoading(true);
    try {
      const res = await getSites(orgId);
      if (fetchId !== lastFetchRef.current.site) return;
      const data = res?.data?.data
        .filter((s) => s.is_active)
        .map((s) => ({
          name: s.name,
          displayName: s.display_name,
          id: s.id,
          metadata: s.metadata,
        }));
      setSiteData(data);
    } catch (err) {
      if (fetchId !== lastFetchRef.current.site) return;
      message.error("Error fetching Sites!");
      setSiteData([]);
    } finally {
      if (fetchId === lastFetchRef.current.site) setSiteLoading(false);
    }
  };

  const fetchCrops = async (siteId) => {
    const fetchId = ++lastFetchRef.current.crop;
    setCropLoading(true);
    try {
      const res = await getCrops(siteId);
      if (fetchId !== lastFetchRef.current.crop) return;
      const data = res?.data?.data
        .filter((c) => c.is_active)
        .map((c) => ({
          name: c.name,
          displayName: c.display_name,
          id: c.id,
        }));
      setCropData(data);
    } catch (err) {
      if (fetchId !== lastFetchRef.current.crop) return;
      setCropData([]);
      // Don't toast here to avoid spam
    } finally {
      if (fetchId === lastFetchRef.current.crop) setCropLoading(false);
    }
  };

  // ------------------------------------
  //  On mount: fetch Orgs or set org
  // ------------------------------------
  useEffect(() => {
    let cancelled = false;
    const fn = async () => {
      if (!userData) return;
      if (userData.is_superuser) {
        await fetchOrgs();
      } else {
        setCurrentOrg(userData.organization_id);
        const res = await getSites(userData.organization_id);
        if (cancelled) return;
        const filteredData = res?.data?.data.filter((s) => s.is_active) || [];
        const mappedData = filteredData.map((s) => ({
          name: s.name,
          displayName: s.display_name,
          id: s.id,
          metadata: s.metadata,
        }));
        setSiteData(mappedData);
      }
    };
    fn();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [userData]);

  // If superuser => fetch sites on org change
  useEffect(() => {
    if (currentOrg && (userData?.is_superuser || userData?.isAdmin)) {
      fetchSites(currentOrg);
    }
    // eslint-disable-next-line
  }, [currentOrg]);

  // Whenever site changes => fetch crops
  useEffect(() => {
    if (currentSite) {
      fetchCrops(currentSite.id);
    } else {
      setCurrentCrop(null);
      setCropData([]);
    }
    // eslint-disable-next-line
  }, [currentSite]);

  // Update siteMetadata and graph data on site/crop
  useEffect(() => {
    if (currentSite && currentCrop) {
      setSiteMetadata(currentSite.metadata[currentCrop.name] || {});
      const metrics = currentSite.metadata[currentCrop.name]?.metrics
        ? Object.entries(currentSite.metadata[currentCrop.name]?.metrics).map(
            ([type, arr]) => ({ type, metrics: arr }),
          )
        : [];
      setMetricData(metrics);

      fetchGraphData();
    } else {
      setMetricData([]);
    }
    // eslint-disable-next-line
  }, [currentSite, currentCrop]);

  // ------------------------------------
  // Default selections: Only set if data present!
  // ------------------------------------
  // 1) Org
  useEffect(() => {
    if (!orgData?.length) return;
    const orgIdFromUrl = searchParams.get("org");
    let selectedId = orgIdFromUrl
      ? orgData.find((o) => o.id === orgIdFromUrl)?.id
      : defaultDropdown?.org;
    if (!selectedId) selectedId = orgData[0].id;
    if (selectedId !== currentOrg) setCurrentOrg(selectedId);
    // eslint-disable-next-line
  }, [orgData]);

  // 2) Site
  useEffect(() => {
    if (!siteData?.length) return;
    const siteIdFromUrl = searchParams.get("site");
    let selectedSite =
      siteIdFromUrl && siteData.find((s) => s.id === siteIdFromUrl);
    if (!selectedSite && defaultDropdown?.[currentOrg]?.site) {
      selectedSite = siteData.find(
        (s) => s.id === defaultDropdown?.[currentOrg]?.site,
      );
    }
    if (!selectedSite) selectedSite = siteData[0];
    if (
      (!currentSite && selectedSite) ||
      (selectedSite && currentSite && selectedSite.id !== currentSite.id)
    ) {
      setCurrentSite(selectedSite);
    }
    // eslint-disable-next-line
  }, [siteData]);

  // 3) Crop
  useEffect(() => {
    if (!cropData?.length) return;
    const cropIdFromUrl = searchParams.get("crop");
    let selectedCrop =
      cropIdFromUrl && cropData.find((c) => c.id === cropIdFromUrl);
    if (!selectedCrop && defaultDropdown?.[currentOrg]?.crop) {
      selectedCrop = cropData.find(
        (c) => c.id === defaultDropdown?.[currentOrg]?.crop,
      );
    }
    if (!selectedCrop) selectedCrop = cropData[0];
    if (
      (!currentCrop && selectedCrop) ||
      (selectedCrop && currentCrop && selectedCrop.id !== currentCrop.id)
    ) {
      setCurrentCrop(selectedCrop);
    }
    // eslint-disable-next-line
  }, [cropData]);

  // 4) Metric (type + actual metric)
  useEffect(() => {
    if (!currentSite || !currentCrop) return;
    // metricType
    const metricTypeFromUrl = searchParams.get("metricType");
    let typeVal = metricTypeFromUrl
      ? metricTypeFromUrl
      : currentSite.metadata[currentCrop.name]?.default_metrics?.[0] || null;
    if (typeVal !== metricType) setMetricType(typeVal);

    // currentMetric
    const metricFromUrl = searchParams.get("metric");
    let metricVal;
    if (metricFromUrl) {
      try {
        metricVal = JSON.parse(metricFromUrl);
      } catch {
        metricVal = metricFromUrl;
      }
    } else {
      metricVal =
        currentSite.metadata[currentCrop.name]?.default_metrics?.[1] || null;
    }
    if (
      (!currentMetric && metricVal) ||
      (metricVal &&
        currentMetric &&
        JSON.stringify(currentMetric) !== JSON.stringify(metricVal))
    ) {
      setCurrentMetric(metricVal);
    }
    // eslint-disable-next-line
  }, [currentSite, currentCrop, metricType, setMetricType, setCurrentMetric]);

  // ------------------------------------
  // Sync changes => localStorage + URL, only if value actually changes!
  // ------------------------------------
  // 1) Org
  useEffect(() => {
    if (!currentOrg) return;
    if (searchParams.get("org") !== currentOrg) {
      const params = new URLSearchParams(searchParams);
      params.set("org", currentOrg);
      setSearchParams(params);
    }
    if (defaultDropdown?.org !== currentOrg) {
      setDefaultDropdown({
        ...defaultDropdown,
        org: currentOrg,
      });
    }
    // eslint-disable-next-line
  }, [currentOrg]);

  // 2) Site
  useEffect(() => {
    if (!currentSite) return;
    if (searchParams.get("site") !== currentSite.id) {
      const params = new URLSearchParams(searchParams);
      params.set("site", currentSite.id);
      setSearchParams(params);
    }
    if (defaultDropdown?.[currentOrg]?.site !== currentSite.id) {
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...(defaultDropdown?.[currentOrg] || {}),
          site: currentSite.id,
        },
      });
    }
    // eslint-disable-next-line
  }, [currentSite]);

  // 3) Crop
  useEffect(() => {
    if (!currentCrop) return;
    if (searchParams.get("crop") !== currentCrop.id) {
      const params = new URLSearchParams(searchParams);
      params.set("crop", currentCrop.id);
      setSearchParams(params);
    }
    if (defaultDropdown?.[currentOrg]?.crop !== currentCrop.id) {
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...(defaultDropdown?.[currentOrg] || {}),
          crop: currentCrop.id,
        },
      });
    }
    // eslint-disable-next-line
  }, [currentCrop]);

  // 4) Metric
  useEffect(() => {
    let needUpdate = false;
    const params = new URLSearchParams(searchParams);
    if (
      currentMetric &&
      params.get("metric") !== JSON.stringify(currentMetric)
    ) {
      params.set("metric", JSON.stringify(currentMetric));
      needUpdate = true;
    }
    if (metricType && params.get("metricType") !== metricType) {
      params.set("metricType", metricType);
      needUpdate = true;
    }
    if (needUpdate) setSearchParams(params);
    // eslint-disable-next-line
  }, [currentMetric, metricType]);

  // Loading
  useEffect(() => {
    setDropdownLoading(orgLoading || siteLoading || cropLoading);
  }, [orgLoading, siteLoading, cropLoading, setDropdownLoading]);

  // ------------------------------------
  // Render (Unchanged)
  // ------------------------------------
  return (
    <>
      {orgLoading || siteLoading ? (
        <LoadingIcon classname="absolute top-0 left-0" height="150px" />
      ) : (
        <div className="w-full flex flex-wrap gap-4 justify-around md:justify-between sm:flex-col mb-10 dropdowns !font-MuseoSans">
          {userData?.is_superuser && (
            <TopDropdown
              label="Select Org:"
              val={currentOrg ?? undefined}
              classString="flex-1 min-w-[200px] sm:mb-2 hover:shadow-2xl transition-shadow duration-300 transform hover:scale-100"
              placeholder="Select Organization"
              changeCallback={setCurrentOrg}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              filterSort={(a, b) =>
                (a?.label ?? "")
                  .toLowerCase()
                  .localeCompare((b?.label ?? "").toLowerCase())
              }
              notFound={
                orgLoading ? (
                  <LoadingIcon
                    classname="absolute top-0 left-0"
                    height="150px"
                  />
                ) : null
              }
              data={orgData.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
            />
          )}

          <TopDropdown
            label="Select Site:"
            val={currentSite?.id ?? undefined}
            classString="flex-1 min-w-[200px] sm:mb-2"
            placeholder="Select Site"
            changeCallback={(siteId) => {
              const siteObj = siteData.find((s) => s.id === siteId);
              setCurrentSite(siteObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a?.label ?? "")
                .toLowerCase()
                .localeCompare((b?.label ?? "").toLowerCase())
            }
            notFound={
              siteLoading ? (
                <LoadingIcon classname="absolute top-0 left-0" height="150px" />
              ) : null
            }
            data={siteData?.map((site) => ({
              value: site.id,
              label: site.displayName,
            }))}
          />

          <TopDropdown
            label="Select Crop:"
            val={currentCrop?.id ?? undefined}
            placeholder="Select Crop"
            classString="flex-1 min-w-[200px] sm:mb-2"
            notFound={
              cropLoading ? (
                <LoadingIcon classname="absolute top-0 left-0" height="150px" />
              ) : null
            }
            changeCallback={(val) => {
              const cropObj = cropData.find((c) => c.id === val);
              setCurrentCrop(cropObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a?.label ?? "")
                .toLowerCase()
                .localeCompare((b?.label ?? "").toLowerCase())
            }
            data={cropData?.map((crop) => ({
              label: crop.displayName,
              value: crop.id,
            }))}
          />

          <div className="flex flex-col">
            <div className="text-sm text-darkerGray max-w-full mb-1">
              Select Metric:
            </div>
            <Cascader
              value={
                metricType && currentMetric
                  ? [
                      metricType,
                      isDictionary(currentMetric)
                        ? currentMetric.value
                        : currentMetric,
                    ]
                  : null
              }
              placeholder="Select Metric"
              className="flex-1 min-w-[200px]"
              onChange={(val) => {
                // val = [metricType, metricValue]
                const [newMetricType, newMetricVal] = val;
                setMetricType(newMetricType);
                const metricObj = metricData.find(
                  (m) => m.type === newMetricType,
                );
                if (!metricObj) {
                  setCurrentMetric(null);
                  return;
                }
                const foundMetric = metricObj.metrics.find((m) => {
                  if (typeof m === "string") {
                    return m === newMetricVal;
                  } else if (isDictionary(m)) {
                    return m.value === newMetricVal;
                  }
                  return false;
                });
                if (foundMetric) {
                  setCurrentMetric(foundMetric);
                } else {
                  setCurrentMetric({
                    value: "unknown",
                    label: "Unknown Metric",
                  });
                }
              }}
              options={metricData?.map((metricObj) => ({
                value: metricObj.type,
                label: metricValToLabel[metricObj.type] || metricObj.type,
                children: metricObj.metrics.map((m) => {
                  if (typeof m === "string") {
                    return {
                      value: m,
                      label: m,
                    };
                  } else if (isDictionary(m)) {
                    return {
                      value: m.value,
                      label: m.label,
                    };
                  } else {
                    return {
                      value: "unknown",
                      label: "Unknown Metric",
                    };
                  }
                }),
              }))}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Dropdowns;
